import { useState, useMemo } from 'react'
import { useAlbums } from '../hooks/useAlbums'
import { useSavedAlbums } from '../hooks/useSavedAlbums'
import { useJobPolling } from '../hooks/useJobPolling'
import { apiClient } from '../api/client'
import { DateRangeFilter } from './DateRangeFilter'
import { AlbumPhotoGrid } from './AlbumPhotoGrid'
import type { Album, AlbumDownloadJob } from '../api/types'

/**
 * アルバム一覧・ダウンロード・閲覧を統合したページコンポーネント
 */
export function AlbumBrowser() {
  const defaultEndDate = new Date().toISOString().split('T')[0]!
  const defaultStartDate = '2020-01-01'

  const [startDate, setStartDate] = useState(defaultStartDate)
  const [endDate, setEndDate] = useState(defaultEndDate)
  const [downloadingAlbumId, setDownloadingAlbumId] = useState<string | null>(null)
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const [currentJobId, setCurrentJobId] = useState<string | null>(null)
  const [selectedAlbumId, setSelectedAlbumId] = useState<string | null>(null)

  const { albums, loading, error, refresh } = useAlbums(startDate, endDate)
  const { albums: savedAlbums, refresh: refreshSaved } = useSavedAlbums()

  // ダウンロード済みアルバムIDのセット
  const savedAlbumIds = useMemo(
    () => new Set(savedAlbums.map((a) => a.album_id)),
    [savedAlbums],
  )

  // ジョブポーリング
  const { job } = useJobPolling(currentJobId, (completedJob: AlbumDownloadJob) => {
    setDownloadingAlbumId(null)
    setCurrentJobId(null)

    if (completedJob.status === 'completed') {
      alert(
        `アルバムのダウンロードが完了しました。\n` +
        `成功: ${completedJob.downloaded_photos}枚\n` +
        (completedJob.failed_photos > 0 ? `失敗: ${completedJob.failed_photos}枚` : '')
      )
      refresh()
      refreshSaved()
    } else if (completedJob.status === 'failed') {
      const message = completedJob.error_message || 'ダウンロードに失敗しました'
      setDownloadError(message)
      alert(`エラー: ${message}`)
    }
  })

  const handleFilterChange = (_start?: string, end?: string) => {
    if (end) {
      setEndDate(end)
      const endDateObj = new Date(end)
      const startDateObj = new Date(endDateObj)
      startDateObj.setDate(startDateObj.getDate() - 30)
      setStartDate(startDateObj.toISOString().split('T')[0]!)
    } else {
      setEndDate(defaultEndDate)
      setStartDate(defaultStartDate)
    }
  }

  const handleDownload = async (album: Album) => {
    setDownloadingAlbumId(album.album_id)
    setDownloadError(null)

    try {
      const response = await apiClient.downloadAlbum(album.album_id)

      if (response.success) {
        setCurrentJobId(response.job_id)
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'ダウンロードジョブの作成に失敗しました'
      setDownloadError(message)
      alert(`エラー: ${message}`)
      setDownloadingAlbumId(null)
    }
  }

  // アルバムが選択されている場合は写真グリッドを表示
  if (selectedAlbumId) {
    const selectedAlbum = albums.find((a) => a.album_id === selectedAlbumId)
      || savedAlbums.find((a) => a.album_id === selectedAlbumId)
    return (
      <AlbumPhotoGrid
        albumId={selectedAlbumId}
        albumTitle={selectedAlbum?.title || ''}
        onBack={() => setSelectedAlbumId(null)}
      />
    )
  }

  return (
    <div className="album-browser">
      <div className="album-browser-header">
        <h2>アルバム</h2>
        <DateRangeFilter onFilterChange={handleFilterChange} />
      </div>

      {loading && <div className="loading">読み込み中...</div>}

      {error && <div className="error-message">{error}</div>}

      {!loading && !error && albums.length === 0 && (
        <div className="empty-state">
          選択した期間にアルバムはありません。
        </div>
      )}

      {!loading && !error && albums.length > 0 && (
        <div className="album-list">
          {albums.map((album) => {
            const isSaved = savedAlbumIds.has(album.album_id)
            const isDownloading = downloadingAlbumId === album.album_id

            return (
              <div key={album.album_id} className="album-item">
                <div className="album-info">
                  <h3 className="album-title">{album.title}</h3>
                  <div className="album-meta">
                    <span className="album-date">{album.date}</span>
                    <span className="album-count">{album.photo_count}枚</span>
                  </div>
                </div>
                <div className="album-actions">
                  {isSaved && (
                    <button
                      onClick={() => setSelectedAlbumId(album.album_id)}
                      className="view-album-button"
                    >
                      アルバムを見る
                    </button>
                  )}
                  <button
                    onClick={() => handleDownload(album)}
                    disabled={isDownloading}
                    className="download-button"
                  >
                    {isDownloading && job
                      ? `処理中 (${job.downloaded_photos}/${job.total_photos}枚)`
                      : isDownloading
                      ? 'ジョブ作成中...'
                      : isSaved
                      ? '再ダウンロード'
                      : 'ダウンロード'}
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {downloadError && (
        <div className="error-message download-error">
          {downloadError}
        </div>
      )}
    </div>
  )
}
